import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { UserGroup } from '@/pages/MeetingDashboard/MeetingDashboard';
import { getMeetingType } from '@/pages/MeetingDashboard/MeetingDashboard';
import { generateRepCoachingInsights, getRepCoachingInsights } from '@/api/tenantDashboardService';
import CoachingModal from './CoachingModal';

// ─── Constants ────────────────────────────────────────────────────────────────

const VALID_PURPOSES = [
  { key: 'DISCOVERY',          label: 'Discovery' },
  { key: 'ROI_DISCUSSION',     label: 'ROI Discussion' },
  { key: 'OBJECTION_HANDLING', label: 'Objection Handling' },
  { key: 'REVIEW',             label: 'Review' },
  { key: 'INTRODUCTION',       label: 'Introduction' },
];

const VALID_OBJECTIVES = [
  { key: 'FOLLOW_UP_CALL', label: 'Follow Up Call' },
  { key: 'PROPOSAL',       label: 'Proposal' },
  { key: 'DEMO',           label: 'Demo' },
  { key: 'NEGOTIATION',    label: 'Negotiation' },
];

// Short narrative copy for stage / contact-type blocks (mirrors the reference UI,
// where each value carries a one-line explanation instead of sitting bare).
const STAGE_DESCRIPTIONS: Record<string, string> = {
  INTRODUCTION:    'Initial contact to understand needs and challenges',
  DISCOVERY:       'Exploring requirements and qualifying fit',
  PROPOSAL:        'Presenting the solution, scope and pricing',
  NEGOTIATION:     'Aligning on terms and commercials before close',
  CLOSING:         'Finalising the agreement and next steps',
  FOLLOW_UP:       'Checking in after a prior conversation',
};

const CONTACT_TYPE_DESCRIPTIONS: Record<string, string> = {
  cold:                   'No prior relationship with this prospect',
  warm:                   'Some prior engagement or referral in place',
  'inbound lead':         'Prospect reached out through website form',
  'existing relationship':'Ongoing relationship with this account',
  'current customer':     'Active customer being engaged for expansion',
  're-engagement':        'Reconnecting after a period of inactivity',
};

// ─── Card mode: 'meeting' uses recorded/tabs, 'pitch' uses scenario/pitch AI ─
export type CardMode = 'meeting' | 'pitch';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const capScore = (s: number) => Math.min(Math.round(s), 100);

const typeAvgScore = (meetings: any[], type: string): number => {
  const filtered = meetings.filter((m) => getMeetingType(m) === type);
  if (!filtered.length) return 0;
  return capScore(
    filtered.reduce((a: number, m: any) => a + (m.overallPercentage || m.score || 0), 0) / filtered.length
  );
};

const formatMeetingTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    cold: 'Cold', warm: 'Warm', 'inbound lead': 'Inbound Lead',
    'existing relationship': 'Existing Relationship', 'current customer': 'Current Customer',
    're-engagement': 'Re-Engagement', recorded: 'Recorded', scenario: 'Scenario',
  };
  return labels[type] || type.split(/[- ]/).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
};

const formatPurposeLabel = (purpose: string): string => {
  const found = VALID_PURPOSES.find((vp) => vp.key === purpose.toUpperCase());
  if (found) return found.label;
  return purpose
    .replace(/[_-]/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

const isRecordedMeeting = (m: any): boolean => {
  const rawType = String(m.otherDetails?.meetingType || m.meetingType || m.meeting_type || '').trim().toLowerCase();
  if (rawType && rawType !== 'recorded') return false;
  return !m.TargetCompanyProfile || !m.TargetCompanyProfile.id;
};

const isScenarioSession = (m: any): boolean =>
  m.isScenario === true || String(getMeetingType ? getMeetingType(m) : m.meetingType || '').toLowerCase() === 'scenario';

const scoreLabel = (s: number) => {
  if (s >= 90) return { text: 'Excellent', color: '#4ADE80' };
  if (s >= 70) return { text: 'Good', color: '#A3E635' };
  if (s >= 50) return { text: 'Fair', color: '#FACC15' };
  return { text: 'Poor', color: '#F87171' };
};

const fmtDate = (iso: string) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const fmtTime = (iso: string) =>
  iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : '';

const fmtDuration = (totalSeconds: number) => {
  if (!totalSeconds || totalSeconds < 0) return '—';
  const m = Math.floor(totalSeconds / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};
 
const getDuration = (m: any): string => {
  // Normalises a raw value to seconds — some sources give ms timestamps
  // instead of seconds, which otherwise blows up into huge mm:ss values.
  const toSeconds = (val: number) => (val > 10800 ? val / 1000 : val); // >3hrs in "seconds" ⇒ treat as ms
 
  const rawSecs = m.duration || m.durationSeconds || m.callDurationSeconds || m.audioDurationSeconds;
  if (rawSecs) return fmtDuration(toSeconds(rawSecs));
 
  if (Array.isArray(m.transcriptJson) && m.transcriptJson.length) {
    const last = m.transcriptJson[m.transcriptJson.length - 1];
    const end = last?.end ?? last?.endTime;
    if (typeof end === 'number') return fmtDuration(toSeconds(end));
  }
  return '—';
};

const getCompanyName = (m: any): string =>
  m.TargetCompanyProfile?.companyName || m.customerData?.companyName || m.companyName || m.company || '';

// ─── Status ───────────────────────────────────────────────────────────────────

const STATUS: Record<string, { color: string; bg: string }> = {
  Active:     { color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
  Processing: { color: '#38bdf8', bg: 'rgba(56,189,248,0.12)' },
  Completed:  { color: '#a78bfa', bg: 'rgba(167,139,250,0.12)' },
  Stalled:    { color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
};

// ─── Filter chip definitions (also used as the "category" each meeting belongs to) ─

const MEETING_TABS = [
  { key: 'cold',                label: 'Cold',                short: 'Cold',       type: 'cold',                color: '#38bdf8' },
  { key: 'warm',                label: 'Warm',                short: 'Warm',       type: 'warm',                color: '#a78bfa' },
  { key: 'inboundLead',         label: 'Inbound Lead',        short: 'Inbound',    type: 'inbound lead',        color: '#f59e0b' },
  { key: 'existingRelationship', label: 'Existing Relationship', short: 'Existing', type: 'existing relationship', color: '#f87171' },
  { key: 'currentCustomer',     label: 'Current Customer',     short: 'Current',    type: 'current customer',     color: '#2dd4bf' },
  { key: 'reEngagement',        label: 'Re-Engagement',       short: 'Re-Engage',  type: 're-engagement',        color: '#60a5fa' },
  { key: 'recorded',            label: 'Recorded',            short: 'Rec',        type: 'recorded',            color: '#22c55e' },
] as const;

const PITCH_MEETING_TABS = [
  ...MEETING_TABS.slice(0, -1),
  { key: 'scenario', label: 'Scenario', short: 'Scen', type: 'scenario', color: '#2dd4bf' },
] as const;

type MeetingTabKey = typeof MEETING_TABS[number]['key'];
type PitchTabKey   = typeof PITCH_MEETING_TABS[number]['key'];
type TabKey = MeetingTabKey | PitchTabKey;

// ─── Score Ring ───────────────────────────────────────────────────────────────

const ScoreRing = ({ score, size = 80 }: { score: number; size?: number }) => {
  const s    = capScore(score);
  const r    = (size / 2) - 7;
  const circ = 2 * Math.PI * r;
  const dash = (s / 100) * circ;
  const { color, text } = scoreLabel(s);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="6"/>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="6"
            strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
            transform={`rotate(-90 ${size/2} ${size/2})`}
            style={{ filter: `drop-shadow(0 0 6px ${color}55)` }}/>
        </svg>
        <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <span style={{ fontSize: size <= 64 ? 15 : 19, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{s}%</span>
        </div>
      </div>
      <span style={{ fontSize: 14, fontWeight: 700, color, }}>{text}</span>
    </div>
  );
};

// ─── Shared visual atoms ──────────────────────────────────────────────────────

const eyebrowStyle: React.CSSProperties = {
  fontSize: 11.5, textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)',
  letterSpacing: '0.14em', fontWeight: 700, margin: '0 0 8px', display: 'block',
};

const badge = (color: string, filled = false): React.CSSProperties => ({
  fontSize: 13, fontWeight: 600, padding: '4px 12px', borderRadius: 999,
  background: filled ? `${color}1f` : 'rgba(255,255,255,0.06)',
  color:      filled ? color        : 'rgba(255,255,255,0.75)',
  border:     `1px solid ${filled ? color + '40' : 'rgba(255,255,255,0.1)'}`,
  display:    'inline-block', lineHeight: 1.4,
});

/** Icon avatar used at the head of each info block — violet/indigo by default,
 *  tinted to the meeting-type colour so each entry stays colour-coded. */
const IconAvatar = ({ color, children }: { color: string; children: React.ReactNode }) => (
  <div style={{
    width: 40, height: 40, borderRadius: 11, flexShrink: 0,
    background: `linear-gradient(135deg, ${color}26, ${color}0c)`,
    border: `1px solid ${color}3d`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    {children}
  </div>
);

const Icon = {
  Person: (c: string) => (
    <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
    </svg>
  ),
  Calendar: (c: string) => (
    <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M4 11h16M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
    </svg>
  ),
  Clock: (c: string) => (
    <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}>
      <circle cx="12" cy="12" r="9"/><path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 3"/>
    </svg>
  ),
  Handshake: (c: string) => (
    <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 11l2.5 2.5L15 9m-9 2l-3 3 3 3 2-2m9-7l3 3-3 3-2-2M6 13l4 4M14 9l4 4"/>
    </svg>
  ),
  Swap: (c: string) => (
    <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h13l-3-3m3 3l-3 3M20 17H7l3 3m-3-3l3-3"/>
    </svg>
  ),
  Refresh: (c: string) => (
    <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h5M20 20v-5h-5M4 9a8 8 0 0114-5M20 15a8 8 0 01-14 5"/>
    </svg>
  ),
  Mic: (c: string) => (
    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"/>
    </svg>
  ),
  Scenario: (c: string) => (
    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
    </svg>
  ),
};

// ─── Single meeting entry card (premium layout, mirrors the reference screenshot) ──

const MeetingEntryCard = ({
  meeting, tabColor, kind,
}: { meeting: any; tabColor: string; kind: 'full' | 'recorded' | 'scenario' }) => {
const navigate = useNavigate();
  const location = useLocation();
  const isSpecial = kind !== 'full';

  const score     = capScore(meeting.overallPercentage || meeting.score || 0);
  const createdAt = meeting.createdAt || meeting.recordedAt || '';
  const status    = meeting.status || 'Active';
  const stCfg     = STATUS[status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()] ?? STATUS.Active;
  const duration  = getDuration(meeting);

  // Full (real meeting) data
  const company   = getCompanyName(meeting);
  const industry  = meeting.TargetCompanyProfile?.industry    || meeting.customerData?.industry    || '';
  const bizModel  = meeting.TargetCompanyProfile?.businessModel || '';
  const contact   = meeting.customerContact?.fullName
    || `${meeting.companyContact?.firstName || ''} ${meeting.companyContact?.lastName || ''}`.trim() || '—';
  const email     = meeting.companyContact?.email || meeting.customerContact?.email || '';
  const jobTitle  = meeting.companyContact?.jobTitle || '';
  const phone     = meeting.companyContact?.phoneNumber || meeting.customerContact?.phoneNumber || '';
  const persona   = meeting.companyContact?.buyerPersona || [];
  const purposes  = meeting.otherDetails?.meetingPurpose || [];
  const stage     = meeting.otherDetails?.stageofSalesCycle || [];
  const objective = meeting.otherDetails?.meetingObjective || [];
  const meetType      = String(getMeetingType ? getMeetingType(meeting) : meeting.meetingType || '').toLowerCase();
  const meetTypeLabel = formatMeetingTypeLabel(meetType);
  const contactDesc    = CONTACT_TYPE_DESCRIPTIONS[meetType] || 'Type of relationship with this prospect';
  const stageLabel      = stage.length ? stage.map((s: string) => s.charAt(0) + s.slice(1).toLowerCase()).join(' / ') : '—';
  const stageDesc        = STAGE_DESCRIPTIONS[(stage[0] || '').toUpperCase()] || 'Current point in the active sales cycle';

  // Recorded / Scenario titles
  const specialTitle = kind === 'scenario'
    ? (meeting.pitchTitle || `Scenario ${(meeting.id || meeting._id || '').slice(0, 8)}`)
    : (meeting.uploaded_audio_url ? meeting.uploaded_audio_url.split('/').pop() : `Recording ${(meeting.id || meeting._id || '').slice(0, 8)}`);

  return (
    <div style={{
      background: 'linear-gradient(160deg, #0d0d16 0%, #0a0a12 100%)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderLeft: `3px solid ${tabColor}`,
      borderRadius: 18,
      padding: '24px 28px',
    }}>
      {/* ── Top row ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 28, flexWrap: 'wrap' }}>

        {/* Identity block */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flex: '1 1 260px', minWidth: 220 }}>
          <IconAvatar color={tabColor}>
            {isSpecial
              ? (kind === 'scenario' ? Icon.Scenario(tabColor) : Icon.Mic(tabColor))
              : Icon.Person(tabColor)}
          </IconAvatar>
          <div style={{ minWidth: 0 }}>
            <span style={eyebrowStyle}>{isSpecial ? (kind === 'scenario' ? 'Scenario' : 'Recording') : 'Buyer Persona'}</span>
            {isSpecial ? (
              <p style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>
                {specialTitle}
              </p>
            ) : (
              <>
                {company && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: 'rgba(255,255,255,0.6)' }}>{company}</span>
                    {industry && <span style={badge(tabColor, false)}>{industry}</span>}
                    {bizModel && <span style={badge(tabColor, false)}>{bizModel}</span>}
                  </div>
                )}
                <p style={{ fontSize: 17, fontWeight: 700, color: '#fff', margin: '0 0 2px' }}>{contact}</p>
                {jobTitle && <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.55)', margin: '0 0 4px' }}>{jobTitle}</p>}
                {email && (
                  <p style={{ fontSize: 13, color: '#38bdf8', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 230 }}>
                    {email}{phone ? ` · ${phone}` : ''}
                  </p>
                )}
                {persona.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {persona.slice(0, 3).map((p: string) => (
                      <span key={p} style={badge(tabColor, true)}>{p.charAt(0) + p.slice(1).toLowerCase()}</span>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Timestamp */}
        <div style={{ flex: '0 0 150px' }}>
          <span style={eyebrowStyle}>Timestamp</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {Icon.Calendar(tabColor)}
            <div>
              <p style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0 }}>{fmtDate(createdAt)}</p>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', margin: 0 }}>{fmtTime(createdAt)}</p>
            </div>
          </div>
        </div>

        {/* Duration */}
        <div style={{ flex: '0 0 110px' }}>
          <span style={eyebrowStyle}>Duration</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {Icon.Clock(tabColor)}
            <p style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0 }}>{duration}</p>
          </div>
        </div>

        {/* Score */}
        <div style={{ flex: '0 0 100px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ ...eyebrowStyle, textAlign: 'center' }}>Pitchster Score</span>
          <ScoreRing score={score} size={72} />
        </div>

        {/* View Details */}
        <div style={{ flex: '0 0 auto', marginLeft: 'auto' }}>
          <button
            onClick={() => {
              const st = (location.state as any) || {};
              const repObj = st.selectedRep || st.rep || (meeting.userId ? { id: meeting.userId, userId: meeting.userId, name: meeting.User?.name || meeting.userName || meeting.salesRep } : null);
              navigate(`/meeting-detail/${meeting.id || meeting._id}`, { state: { ...st, meeting, from: location.pathname + location.search, selectedRep: repObj, repTab: 'meetings' } });
            }}
            style={{
              background: `linear-gradient(135deg, ${tabColor}18, ${tabColor}08)`,
              border: `1px solid ${tabColor}45`, color: tabColor,
              padding: '10px 18px', borderRadius: 11, fontSize: 13.5, fontWeight: 700,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap',
            }}
          >
            View Details
            <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12"/>
            </svg>
          </button>
        </div>
      </div>

      {/* ── Divider ── */}
      <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', margin: '20px 0' }}/>

      {/* ── Bottom row ── */}
      <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-start' }}>

        {!isSpecial && stage.length > 0 && (
          <div style={{ display: 'flex', gap: 12, flex: '1 1 220px', minWidth: 200 }}>
            <IconAvatar color={tabColor}>{Icon.Handshake(tabColor)}</IconAvatar>
            <div>
              <span style={eyebrowStyle}>Stage of Sales Cycle</span>
              <p style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: '0 0 3px' }}>{stageLabel}</p>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', margin: 0, lineHeight: 1.4 }}>{stageDesc}</p>
            </div>
          </div>
        )}

        {!isSpecial && (
          <div style={{ display: 'flex', gap: 12, flex: '1 1 220px', minWidth: 200 }}>
            <IconAvatar color={tabColor}>{Icon.Swap(tabColor)}</IconAvatar>
            <div>
              <span style={eyebrowStyle}>Type of Contact</span>
              <p style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: '0 0 3px' }}>{meetTypeLabel}</p>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', margin: 0, lineHeight: 1.4 }}>{contactDesc}</p>
            </div>
          </div>
        )}

        {!isSpecial && (purposes.length > 0 || objective.length > 0) && (
          <div style={{ flex: '1 1 220px', minWidth: 200, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {purposes.length > 0 && (
              <div>
                <span style={eyebrowStyle}>Type of Interaction</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {purposes.map((p: string) => <span key={p} style={badge(tabColor, true)}>{formatPurposeLabel(p)}</span>)}
                </div>
              </div>
            )}
            {objective.length > 0 && (
              <div>
                <span style={eyebrowStyle}>Objective</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {objective.map((o: string) => {
                    const found = VALID_OBJECTIVES.find((vo) => vo.key === o.toUpperCase());
                    return <span key={o} style={badge(tabColor, false)}>{found ? found.label : o.replace(/_/g, ' ')}</span>;
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Status + Updated — always shown */}
        <div style={{ flex: '0 0 150px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <span style={eyebrowStyle}>Status</span>
            <span style={{
              fontSize: 13, fontWeight: 700, padding: '4px 13px', borderRadius: 999,
              background: stCfg.bg, color: stCfg.color, border: `1px solid ${stCfg.color}35`,
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: stCfg.color }}/>
              {status}
            </span>
          </div>
          {/* <div>
            <span style={eyebrowStyle}>Updated</span>
            <p style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: 0 }}>{fmtDate(updatedAt)}</p>
            <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.5)', margin: 0 }}>{fmtTime(updatedAt)}</p>
          </div> */}
        </div>
      </div>
    </div>
  );
};

// ─── AI Coaching Modal (Pitch mode only) ─────────────────────────────────────

// ─── Main Unified Card (one card per rep — no collapse) ──────────────────────

interface MeetingCardMainProps {
  group: UserGroup;
  mode?: CardMode;
}

export const MeetingCardMain: React.FC<MeetingCardMainProps> = ({ group, mode = 'meeting' }) => {
  const [coachOpen,    setCoachOpen]    = useState(false);
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachError,   setCoachError]   = useState<string | null>(null);
  const [coachData,    setCoachData]    = useState<any | null>(null);

  const TABS = mode === 'pitch' ? PITCH_MEETING_TABS : MEETING_TABS;
  const specialTabKey = mode === 'pitch' ? 'scenario' : 'recorded';

  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedCompany, setSelectedCompany] = useState<string>('all');

  const isSpecialMeeting = (m: any) => (mode === 'pitch' ? isScenarioSession(m) : isRecordedMeeting(m));

  const getTabKeyForMeeting = (m: any): TabKey | null => {
    if (isSpecialMeeting(m)) return specialTabKey as TabKey;
    const mType = (getMeetingType ? getMeetingType(m) : m.meetingType || m.otherDetails?.meetingType || '').toLowerCase();
    if (['call', 'demo', 'negotiation', 'nego', 'objection', 'obj', 'cold'].includes(mType)) {
      return 'cold';
    }
    const found = TABS.find((t) => t.type === mType);
    return found ? (found.key as TabKey) : 'cold';
  };

  const categoryTabs = useMemo(() => TABS.filter((t) => t.key !== specialTabKey), [TABS, specialTabKey]);
  const nonSpecialMeetings = useMemo(() => group.meetings.filter((m) => !isSpecialMeeting(m)), [group.meetings, mode]);

  const allMeetingsAvgScore = useMemo(() => {
    if (!nonSpecialMeetings.length) return 0;
    return capScore(nonSpecialMeetings.reduce((a: number, m: any) => a + (m.overallPercentage || m.score || 0), 0) / nonSpecialMeetings.length);
  }, [nonSpecialMeetings]);

  // Status
  const statusCounts: Record<string, number> = {};
  group.meetings.forEach((m) => {
    const s = m.status
      ? m.status.charAt(0).toUpperCase() + m.status.slice(1).toLowerCase()
      : 'Active';
    statusCounts[s] = (statusCounts[s] || 0) + 1;
  });
  const dominantStatus = Object.entries(statusCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'Active';
  const stCfg = STATUS[dominantStatus] ?? STATUS.Active;

  const tabCount = useMemo(() => {
    const counts: Record<string, number> = {
      cold: 0,
      warm: 0,
      inboundLead: 0,
      existingRelationship: 0,
      currentCustomer: 0,
      reEngagement: 0,
      recorded: 0,
      scenario: 0,
    };
    group.meetings.forEach((m) => {
      const key = getTabKeyForMeeting(m);
      if (key && key in counts) {
        counts[key]++;
      }
    });
    return counts;
  }, [group.meetings]);

  const specialAvgScore = useMemo(() => {
    const items = group.meetings.filter(isSpecialMeeting);
    if (!items.length) return 0;
    return capScore(items.reduce((a: number, m: any) => a + (m.overallPercentage || m.score || 0), 0) / items.length);
  }, [group.meetings, mode]);

  // Company options — derived only from meetings that actually carry company data
  const companyOptions = useMemo(() => {
    const set = new Set<string>();
    group.meetings.forEach((m) => {
      if (!isSpecialMeeting(m)) {
        const c = getCompanyName(m);
        if (c) set.add(c);
      }
    });
    return Array.from(set).sort();
  }, [group.meetings, mode]);


  const resetFilters = () => {
    setSelectedType('all');
    setSelectedCompany('all');
  };

  const visibleMeetings = useMemo(() => {
    return group.meetings
      .filter((m) => {
        const isSpecial = isSpecialMeeting(m);
        if (selectedType === specialTabKey) {
          if (!isSpecial) return false;
        } else if (selectedType === 'all') {
          if (isSpecial) return false;
          if (selectedCompany !== 'all' && getCompanyName(m) !== selectedCompany) return false;
        } else {
          if (isSpecial) return false;
          const key = getTabKeyForMeeting(m);
          if (key !== selectedType) return false;
          if (selectedCompany !== 'all' && getCompanyName(m) !== selectedCompany) return false;
        }
        return true;
      })
      .sort((a: any, b: any) => {
        const da = new Date(a.createdAt || a.recordedAt || 0).getTime();
        const db = new Date(b.createdAt || b.recordedAt || 0).getTime();
        return db - da;
      });
  }, [group.meetings, selectedType, selectedCompany, mode, specialTabKey]);

  const latestDate = group.latestMeetingDate
    ? new Date(group.latestMeetingDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

  // ── AI Coaching open handler (pitch mode only) ──
  const openCoachingModal = async () => {
    if (!group.userId) return;
    setCoachOpen(true);
    setCoachError(null);
    setCoachData(null);
    setCoachLoading(true);
    try {
      await generateRepCoachingInsights(group.userId);
      const data = await getRepCoachingInsights(group.userId);
      if (data?.status === 'done' && data?.hasInsights) {
        setCoachData(data);
        setCoachLoading(false);
      } else {
        setCoachLoading(false);
        setCoachError('Insights not ready. Please try again shortly.');
      }
    } catch (err: any) {
      setCoachLoading(false);
      const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to generate insights.';
      if ((err?.response?.status ?? err?.status) === 400) {
        setCoachData({ status: 'done', hasInsights: true, insights: { overallAssessment: msg, quickWin: 'Complete more sessions to unlock AI coaching.' } });
      } else {
        setCoachError(msg);
      }
    }
  };

  return (
    <>
      <div style={{
        background: 'linear-gradient(160deg, #09090f 0%, #07070d 100%)',
        border: '1px solid rgba(255,255,255,0.09)',
        borderRadius: 20,
        marginBottom: 16,
        overflow: 'hidden',
        boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
      }}>

        {/* ══ HEADER (always visible — no collapse) ══ */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 20, padding: '22px 26px',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
        }}>
          <ScoreRing score={group.avgScore} size={76} />

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 5 }}>
              <span style={{ fontSize: 18, fontWeight: 700, color: '#ffffff' }}>{group.userName}</span>
              <span style={{
                fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999,
                background: stCfg.bg, color: stCfg.color, border: `1px solid ${stCfg.color}35`,
                display: 'flex', alignItems: 'center', gap: 5,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: stCfg.color }}/>
                {dominantStatus}
              </span>
              {group.activeCount > 0 && (
                <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999, background: 'rgba(52,211,153,0.1)', color: '#34d399', border: '1px solid rgba(52,211,153,0.28)' }}>
                  {group.activeCount} live
                </span>
              )}
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 17, color: '#fff', fontWeight: 500 }}>
                {group.totalMeetings} {mode === 'pitch' ? 'pitch' : 'session'}{group.totalMeetings !== 1 ? 'es' : ''}
              </span>
              <span style={{ color: '#374151' }}>·</span>
              <span style={{ fontSize: 17, color: '#fff' }}>{latestDate}</span>
              {visibleMeetings.length !== group.meetings.length && (
                <>
                  <span style={{ color: '#374151' }}>·</span>
                  <span style={{ fontSize: 15, color: 'rgba(255,255,255,0.5)' }}>{visibleMeetings.length} shown</span>
                </>
              )}
            </div>
          </div>
          
          {mode === 'pitch' && (
            <button
              onClick={openCoachingModal}
              style={{
                display: 'flex', alignItems: 'center', gap: 9, flexShrink: 0,
                padding: '11px 20px', borderRadius: 12, fontSize: 13.5, fontWeight: 700,
                background: 'linear-gradient(135deg, rgba(184,248,144,0.12), rgba(184,248,144,0.05))',
                color: '#B8F890', border: '1px solid rgba(184,248,144,0.28)',
                cursor: 'pointer', boxShadow: '0 4px 20px rgba(184,248,144,0.1)', whiteSpace: 'nowrap',
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#B8F890" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
              AI Recommendation
            </button>
          )}
        </div>

        {/* ══ FILTER BAR ══ */}
        <div style={{ padding: '16px 26px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>

          {/* Company filter */}
          {companyOptions.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="rgba(255,255,255,0.45)" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4M9 9v.01M9 12v.01M9 15v.01"/>
              </svg>
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                style={{
                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                  color: '#fff', fontSize: 13.5, fontWeight: 600, padding: '8px 12px',
                  borderRadius: 10, cursor: 'pointer', outline: 'none',
                }}
              >
                <option value="all" style={{ background: '#0d0d16' }}>All Companies</option>
                {companyOptions.map((c) => (
                  <option key={c} value={c} style={{ background: '#0d0d16' }}>{c}</option>
                ))}
              </select>
            </div>
          )}

          {/* 6-Category Dropdown Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="rgba(255,255,255,0.45)" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
            </svg>
            <select
              value={selectedType === specialTabKey ? 'all' : selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              style={{
                background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                color: '#fff', fontSize: 13.5, fontWeight: 600, padding: '8px 12px',
                borderRadius: 10, cursor: 'pointer', outline: 'none',
              }}
            >
              <option value="all" style={{ background: '#0d0d16' }}>All Categories</option>
              {categoryTabs.map((cat) => (
                <option key={cat.key} value={cat.key} style={{ background: '#0d0d16' }}>
                  {cat.label} ({tabCount[cat.key] ?? 0})
                </option>
              ))}
            </select>
          </div>

          <div style={{ width: 1, height: 22, background: 'rgba(255,255,255,0.08)' }}/>

          {/* 2 Simple Chips: All Meetings + Recorded/Scenario */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {/* CHIP 1: All Meetings Simple Chip */}
            {(() => {
              const isAllActive = selectedType !== specialTabKey;
              const blueColor = '#38bdf8';

              const selectedCategoryTab = categoryTabs.find((t) => t.key === selectedType);
              const labelText = selectedType === 'all' ? 'All Meetings' : (selectedCategoryTab?.label || 'All Meetings');

              const count =
                selectedType === 'all'
                  ? nonSpecialMeetings.length
                  : (tabCount[selectedType] ?? 0);

              const avgScore =
                selectedType === 'all'
                  ? allMeetingsAvgScore
                  : typeAvgScore(group.meetings, selectedCategoryTab?.type || '');

              return (
                <button
                  onClick={() => setSelectedType('all')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    padding: '8px 14px',
                    borderRadius: 999,
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    background: isAllActive ? `${blueColor}16` : 'rgba(255,255,255,0.03)',
                    color: isAllActive ? blueColor : 'rgba(255,255,255,0.4)',
                    border: `1px solid ${isAllActive ? blueColor + '45' : 'rgba(255,255,255,0.07)'}`,
                  }}
                >
                  {labelText}
                  <span
                    style={{
                      fontSize: 11.5,
                      fontWeight: 800,
                      padding: '2px 8px',
                      borderRadius: 999,
                      background: isAllActive ? `${blueColor}28` : 'rgba(255,255,255,0.06)',
                      color: isAllActive ? blueColor : 'rgba(255,255,255,0.4)',
                    }}
                  >
                    {count}
                  </span>
                  {count > 0 && isAllActive && (
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: `${blueColor}cc` }}>
                      {avgScore}%
                    </span>
                  )}
                </button>
              );
            })()}

            {/* CHIP 2: Recorded (or Scenario in pitch mode) */}
            {(() => {
              const isSpecialActive = selectedType === specialTabKey;
              const specialLabel = mode === 'pitch' ? 'Scenario' : 'Recorded';
              const specialColor = mode === 'pitch' ? '#2dd4bf' : '#22c55e';
              const cnt = tabCount[specialTabKey] ?? 0;

              return (
                <button
                  onClick={() => setSelectedType(specialTabKey)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    padding: '8px 14px',
                    borderRadius: 999,
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    background: isSpecialActive ? `${specialColor}16` : 'rgba(255,255,255,0.03)',
                    color: isSpecialActive ? specialColor : 'rgba(255,255,255,0.4)',
                    border: `1px solid ${isSpecialActive ? specialColor + '45' : 'rgba(255,255,255,0.07)'}`,
                  }}
                >
                  {specialLabel}
                  <span
                    style={{
                      fontSize: 11.5,
                      fontWeight: 800,
                      padding: '2px 8px',
                      borderRadius: 999,
                      background: isSpecialActive ? `${specialColor}28` : 'rgba(255,255,255,0.06)',
                      color: isSpecialActive ? specialColor : 'rgba(255,255,255,0.4)',
                    }}
                  >
                    {cnt}
                  </span>
                  {cnt > 0 && isSpecialActive && (
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: `${specialColor}cc` }}>
                      {specialAvgScore}%
                    </span>
                  )}
                </button>
              );
            })()}
          </div>

          <button
            onClick={resetFilters}
            style={{
              marginLeft: 'auto', background: 'transparent', border: 'none',
              color: 'rgba(255,255,255,0.4)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              padding: '6px 4px', display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            {Icon.Refresh('rgba(255,255,255,0.4)')}
            Reset
          </button>
        </div>

        {/* ══ MEETING LIST ══ */}
        <div style={{ padding: '20px 26px 26px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {visibleMeetings.length === 0 ? (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', height: 160,
              background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)',
              borderRadius: 16, color: 'rgba(255,255,255,0.5)', fontSize: 15, fontWeight: 600,
            }}>
              No sessions match the selected filters
            </div>
          ) : (
            visibleMeetings.map((m: any) => {
              const key   = getTabKeyForMeeting(m) as TabKey;
              const tab   = TABS.find((t) => t.key === key);
              const color = tab?.color || '#a78bfa';
              const kind: 'full' | 'recorded' | 'scenario' = key === specialTabKey
                ? (mode === 'pitch' ? 'scenario' : 'recorded')
                : 'full';
              return (
                <MeetingEntryCard key={m.id || m._id} meeting={m} tabColor={color} kind={kind} />
              );
            })
          )}
        </div>
      </div>

      {/* AI Modal */}
      {mode === 'pitch' && (
        <CoachingModal
          open={coachOpen}
          onClose={() => setCoachOpen(false)}
          loading={coachLoading}
          error={coachError}
          coachData={coachData}
          group={group}
        />
      )}
    </>
  );
};

export default MeetingCardMain;